import { useEffect, useState } from 'react';
import apiClient from '../utils/axios';
import { Zap, TrendingUp, TrendingDown, Activity, Clock, CheckCircle2, AlertTriangle, XCircle, Building2 } from 'lucide-react';
import CompanyDomainAssignments from '../components/CompanyDomainAssignments';

export default function Domains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainDetails, setDomainDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('domains'); // 'domains' or 'assignments'

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/instantly/domains');
      setDomains(response.data.domains || []);
    } catch (error) {
      console.error('Error fetching domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDomainDetails = async (domainName) => {
    try {
      const response = await apiClient.get(`/instantly/domains/${encodeURIComponent(domainName)}`);
      setDomainDetails(response.data);
    } catch (error) {
      console.error('Error fetching domain details:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle2 size={20} className="text-green-600" />;
      case 'warning':
        return <AlertTriangle size={20} className="text-yellow-600" />;
      case 'unhealthy':
        return <XCircle size={20} className="text-red-600" />;
      default:
        return <Activity size={20} className="text-gray-400" />;
    }
  };

  const getWarmupStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'complete':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Domains</h1>
        <button
          onClick={fetchDomains}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <Activity size={16} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('domains')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'domains'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Domain Health
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'assignments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building2 size={16} className="inline mr-2" />
            Company Assignments
          </button>
        </nav>
      </div>

      {activeTab === 'assignments' ? (
        <CompanyDomainAssignments />
      ) : (
        <>
      {loading ? (
        <div className="text-center p-8">Loading domains...</div>
      ) : domains.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No domains found. Domains will appear here once they're added to Instantly.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {domains.map((domain) => (
            <div
              key={domain.domain_name}
              className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => {
                setSelectedDomain(domain.domain_name);
                fetchDomainDetails(domain.domain_name);
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  {getStatusIcon(domain.status)}
                  <h3 className="text-lg font-semibold text-gray-900">{domain.domain_name}</h3>
                </div>
                {domain.instantly_warmup_status && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getWarmupStatusColor(domain.instantly_warmup_status)}`}>
                    {domain.instantly_warmup_status}
                  </span>
                )}
              </div>

              {/* Health Score */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">Health Score</span>
                  <span className={`text-2xl font-bold ${getHealthScoreColor(domain.health_score || 0)}`}>
                    {domain.health_score || 'N/A'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      (domain.health_score || 0) >= 85
                        ? 'bg-green-600'
                        : (domain.health_score || 0) >= 70
                        ? 'bg-yellow-600'
                        : 'bg-red-600'
                    }`}
                    style={{ width: `${Math.min(100, domain.health_score || 0)}%` }}
                  />
                </div>
              </div>

              {/* Instantly Warmup Progress */}
              {domain.instantly_warmup_status && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Zap size={14} />
                      Warmup Progress
                    </span>
                    <span className="text-sm font-semibold">{domain.instantly_warmup_progress || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${Math.min(100, domain.instantly_warmup_progress || 0)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Emails Today</div>
                  <div className="text-lg font-semibold">
                    {domain.instantly_emails_sent_today || domain.emails_sent_today || 0}
                    {domain.instantly_daily_limit && (
                      <span className="text-xs text-gray-400"> / {domain.instantly_daily_limit}</span>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Bounce Rate</div>
                  <div className="text-lg font-semibold">
                    {domain.bounce_rate ? `${parseFloat(domain.bounce_rate).toFixed(1)}%` : 'N/A'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Inbox Placement</div>
                  <div className="text-lg font-semibold">
                    {domain.inbox_placement_rate ? `${parseFloat(domain.inbox_placement_rate).toFixed(1)}%` : 'N/A'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Spam Rate</div>
                  <div className="text-lg font-semibold">
                    {domain.spam_complaint_rate ? `${parseFloat(domain.spam_complaint_rate).toFixed(2)}%` : 'N/A'}
                  </div>
                </div>
              </div>

              {domain.last_checked && (
                <div className="mt-4 flex items-center gap-1 text-xs text-gray-400">
                  <Clock size={12} />
                  <span>Last checked: {new Date(domain.last_checked).toLocaleString()}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Domain Details Modal */}
      {selectedDomain && domainDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">{selectedDomain}</h2>
              <button
                onClick={() => {
                  setSelectedDomain(null);
                  setDomainDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Instantly Data</h3>
                <pre className="bg-gray-50 p-4 rounded text-xs overflow-auto">
                  {JSON.stringify(domainDetails.instantly, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

